/**
 * Creference Component
 * Inline entity reference with icon + clickable accent text
 */

"use client";

import { type CSSProperties, type ReactNode } from "react";
import type { DataElScope } from "@/utils/dataEl";
import { CreferenceContent } from "./CreferenceContent";
import { CreferenceMode } from "./CreferenceMode";
import { CreferenceNestedLabel } from "./CreferenceNestedLabel";
import {
  CreferenceRenderMode,
  useCreferenceRenderMode,
} from "./CreferenceRenderMode";
import {
  CreferenceDepthBoundary,
  useIsNestedCreference,
} from "./creferenceDepth";
import { buildCreferenceInteractionInput } from "./creferenceInteractionInput";
import { composeCreferenceLabel } from "./creferenceResolvedEntitySelectors";
import { useCreferenceInteraction } from "./useCreferenceInteraction";
import { useCreferenceResolvedEntity } from "./useCreferenceResolvedEntity";

export { CreferenceMode } from "./CreferenceMode";

export interface CreferenceProps {
  dataElScope: DataElScope;
  /** Canonical short-id string (`p3`, `v7`, `m2`, `c5`, `u1`, …) — or the
   *  version `formatted` alias `p3/4`. The resolver consults the unified
   *  provider index to determine the entity kind and label. */
  entityId: string;
  mode?: CreferenceMode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

export function Creference({
  dataElScope,
  ...props
}: CreferenceProps): ReactNode {
  const renderMode = useCreferenceRenderMode();

  if (renderMode === CreferenceRenderMode.Literal) {
    return (
      <CreferenceNestedLabel
        className={props.className}
        label={`[[${props.entityId}]]`}
        style={props.style}
      />
    );
  }

  return <ResolvedCreference {...props} dataElScope={dataElScope} />;
}

function ResolvedCreference({
  dataElScope,
  entityId,
  mode = CreferenceMode.Pill,
  onClick,
  disabled = false,
  className,
  style,
}: CreferenceProps): ReactNode {
  const isNested = useIsNestedCreference();
  const resolvedEntity = useCreferenceResolvedEntity({ entityId });
  const interaction = useCreferenceInteraction(
    buildCreferenceInteractionInput({
      disabled,
      onClick,
      resolvedEntity,
    }),
  );

  // A reference inside another reference's label resolves to plain text. It is
  // the recursion terminator, not a styling choice — see `creferenceDepth.tsx`.
  if (isNested) {
    return (
      <CreferenceNestedLabel
        className={className}
        label={composeCreferenceLabel({
          label: resolvedEntity.label,
          labelSuffix: resolvedEntity.labelSuffix,
        })}
        style={style}
      />
    );
  }

  return (
    <CreferenceDepthBoundary>
      <CreferenceContent
        dataElScope={dataElScope}
        className={className}
        disabled={disabled}
        interaction={interaction}
        mode={mode}
        resolvedEntity={resolvedEntity}
        style={style}
      />
    </CreferenceDepthBoundary>
  );
}
